import { useDraggable, useDroppable } from '@dnd-kit/core';
import { ArrowLeftRight, GripVertical } from 'lucide-react';
import type { PlayEvent, PlayEventParticipant, PlayerRatingSettings } from '@court-sync/shared';
import {
  formatPlayerRating,
  getPlayEventParticipantDisplayName,
  getPlayEventParticipantSinglesPreferenceLabel,
} from '@court-sync/shared';
import { PlayEventDrawPlayerLine } from '@/components/play/PlayEventDrawPlayerLine';
import { cn } from '@/lib/utils';

interface PlayEventDrawPlayerSlotProps {
  participant: PlayEventParticipant;
  event: PlayEvent;
  organizationId: string;
  eventId: string;
  ratingSettings: PlayerRatingSettings | null;
  editable: boolean;
  draggingParticipantId?: string | null;
  onUpdated?: () => void;
  /** When false, hides the default "Prefers doubles" label in prefer-doubles events. */
  showSinglesPreference?: boolean;
}

export function PlayEventDrawPlayerDragGhost({
  participant,
  event,
  ratingSettings,
}: {
  participant: PlayEventParticipant;
  event: PlayEvent;
  ratingSettings: PlayerRatingSettings | null;
}) {
  const displayName = getPlayEventParticipantDisplayName(participant);
  const ratingLabel = ratingSettings?.label ?? 'Rating';
  const preferenceLabel = getPlayEventParticipantSinglesPreferenceLabel(
    event,
    participant.prefersSingles
  );

  return (
    <div className="flex min-w-[14rem] max-w-xs cursor-grabbing items-start gap-2 rounded-lg border-2 border-primary-500 bg-background px-3 py-2.5 shadow-xl ring-4 ring-primary-500/20">
      <GripVertical className="mt-0.5 h-4 w-4 shrink-0 text-primary-600" aria-hidden />
      <div className="min-w-0">
        <p className="truncate font-medium text-foreground">{displayName}</p>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
          {participant.playerRating != null ? (
            <span>
              {ratingLabel}{' '}
              <span className="font-medium text-foreground">
                {formatPlayerRating(participant.playerRating)}
              </span>
            </span>
          ) : null}
          {preferenceLabel ? <span>{preferenceLabel}</span> : null}
        </div>
      </div>
    </div>
  );
}

export function PlayEventDrawPlayerSlot({
  participant,
  event,
  organizationId,
  eventId,
  ratingSettings,
  editable,
  draggingParticipantId = null,
  onUpdated,
  showSinglesPreference = true,
}: PlayEventDrawPlayerSlotProps) {
  const { attributes, listeners, setNodeRef: setDragRef, isDragging } = useDraggable({
    id: participant.id,
    disabled: !editable,
    data: { participantId: participant.id },
  });
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `drop-${participant.id}`,
    disabled: !editable,
    data: { participantId: participant.id },
  });

  const setRefs = (node: HTMLDivElement | null) => {
    setDragRef(node);
    setDropRef(node);
  };

  const isDropTarget =
    Boolean(isOver && draggingParticipantId && draggingParticipantId !== participant.id);

  return (
    <div
      ref={setRefs}
      className={cn(
        'relative flex items-start gap-2 rounded-md border px-2 py-1.5 transition-all duration-150',
        editable && !isDragging && !isDropTarget && 'border-transparent hover:border-border hover:bg-background/80',
        isDragging && 'border-dashed border-muted-foreground/30 bg-muted/20 opacity-35',
        isDropTarget &&
          'z-10 scale-[1.02] border-primary-500 bg-primary-50 shadow-md ring-2 ring-primary-500/40 dark:bg-primary-500/10 dark:ring-primary-400/50'
      )}
    >
      {isDropTarget ? (
        <span className="pointer-events-none absolute -top-2 right-2 inline-flex items-center gap-1 rounded-full bg-primary-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white shadow-sm">
          <ArrowLeftRight className="h-3 w-3" aria-hidden />
          Swap
        </span>
      ) : null}
      {editable ? (
        <button
          type="button"
          className="mt-0.5 shrink-0 cursor-grab touch-none rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground active:cursor-grabbing"
          aria-label="Drag to swap player"
          {...listeners}
          {...attributes}
        >
          <GripVertical className="h-4 w-4" aria-hidden />
        </button>
      ) : null}
      <div className="min-w-0 flex-1">
        <PlayEventDrawPlayerLine
          participant={participant}
          event={event}
          organizationId={organizationId}
          eventId={eventId}
          ratingSettings={ratingSettings}
          onUpdated={onUpdated}
          compact
          showSinglesPreference={showSinglesPreference}
        />
      </div>
    </div>
  );
}

export function playEventDrawDropParticipantId(overId: string | number): string | null {
  const id = String(overId);
  if (id.startsWith('drop-')) {
    return id.slice(5);
  }
  return id;
}
